import { CalendarDay } from '@shared/models/dateTypes';

/**
 * Who a section or article is written for. Administrators and association logins (foreningsansvarlig,
 * foreningsbooker) use different parts of the app, so each side only sees its own guides; unset
 * means both.
 */
export type HelpAudience = 'admin' | 'association';

/** Every feature a manifest entry or a `::: feature` block may name. */
export const HELP_FEATURES = ['kmd_menu_links', 'kmd_links'] as const;

/**
 * A part of the app that a tenant has, or has not, switched on for a login — as opposed to a
 * permission a role holds. A guide tied to a feature is shown exactly where the feature is on, so
 * it is never written for a menu item the reader cannot see.
 * - `kmd_menu_links`: the organisation's own KMD pages are in the menu (Min forening →
 *   Grundoplysninger, Kontaktpersoner, …): the tenant has them on for this login and KMD has
 *   them for the organisation's type (`KmdMenuLinksService.showOwnPages`), and the role holds
 *   the permission behind them.
 * - `kmd_links`: the menu has KMD links at all for this login — Startside, Tilskud, Min konto
 *   (`KmdMenuLinksService.showOwnLinks`). On for an internal customer's person too, whose own
 *   panel has no KMD pages. No permission: Startside and Min konto need none.
 */
export type HelpFeature = (typeof HELP_FEATURES)[number];

/**
 * The permission the role must hold on top of the tenant's switch for a feature, the same one the
 * menu item is gated on. Asked with the manifest's keys. A feature without one needs only the switch.
 */
export const HELP_FEATURE_PERMISSIONS: Readonly<Partial<Record<HelpFeature, string>>> = {
  kmd_menu_links: 'view_kmd_association_pages',
};

/**
 * One help article as the manifest describes it. The markdown itself lives in
 * `assets/help/<section>/<slug>.md` and is fetched when the article is opened.
 */
export interface HelpArticleMeta {
  /** URL segment and file name, e.g. `opret-booking` */
  slug: string;
  title: string;
  /** One line shown on the overview and in search results */
  summary: string;
  /**
   * Permission key from `permission.Name` (as `UserService.canI` takes it). Unset means every
   * signed-in user sees the article. A section's permission applies to all its articles.
   */
  permission?: string;
  audience?: HelpAudience;
  /** Only listed, searched and served where the feature is on. A section's feature applies to all its articles. */
  feature?: HelpFeature;
  /** For a release note under Nyheder: the day it went to production. Shown next to the title. */
  date?: CalendarDay;
}

export interface HelpSection {
  slug: string;
  title: string;
  /** Material icon name shown next to the section title */
  icon: string;
  permission?: string;
  audience?: HelpAudience;
  feature?: HelpFeature;
  articles: HelpArticleMeta[];
}

/** One search hit: the article plus a snippet of the text around the first match. */
export interface HelpSearchHit {
  section: HelpSection;
  article: HelpArticleMeta;
  excerpt: string;
}
